/**
 * КАРТКА ОРГАНІЗАЦІЇ — повноцінна сторінка (рішення власника 2026-08-14:
 * «карточки — обычными страницами, а не огрызками»; раніше — Drawer
 * поверх списку). Маршрути: /organizations/new і /organizations/:id.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import OrgTaxAccountsTable from '@/components/OrgTaxAccountsTable';
import OrgActivityPlacesTable from '@/components/OrgActivityPlacesTable';
import OrgNameFields, { OrgNamePreview } from '@/components/OrgNameFields';
import { AxiosError } from 'axios';
import {
  orgs, type Organization, type OrganizationCreate, type OrgTaxStatus,
} from '@/api/catalogs';
import { taxOffices } from '@/api/taxOffices';
import { attachments } from '@/api/attachments';
import { resolveOrgOfficials } from '@/api/hr';
import { refs, type BankAccount } from '@/api/refs';
import { BankAccountDuplicates } from '@/shared/components/BankAccountDuplicates';
import { businessTemplates, type AffectedDocs } from '@/api/admin';
import OrgStamps from '@/shared/components/OrgStamps';
import SignControlSettings, {
  SIGN_CONTROL_PENDING_SLOT, saveSignControlPending,
} from '@/shared/components/SignControlSettings';
import KatottgPicker from '@/shared/components/KatottgPicker';
import DatePopover from '@/components/DatePopover';
import { useAllPermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { OrgWarehousesTab, OrgCashRegistersTab } from '@/pages/organizations/OrgRefsTabs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import {
  DraftSlotsProvider, confirmPendingRows, useDraftSlot, usePendingMarker, withLegacyForm,
  type DraftSlots,
} from '@/shared/hooks/draftSlots';
import { signalUserEdit } from '@/store/formDrafts';
import { ConfirmModuleNarrowingModal } from '@/components/ConfirmModuleNarrowingModal';
import {
  deriveOwnName,
  findBankByIban,
  formatIban,
  isValidUaIban,
  type BankRow,
} from '@/shared/ukr_classifiers';
import TchTable from '@/shared/components/TchTable';
import VatPayerSelect, {
  vatAfterTaxSystem, vatFixedFor, vatPayerProblem,
} from '@/shared/components/VatPayerSelect';
import OrgTaxHistoryTable, {
  currentIndex, historyProblem, lockedBeforeOf, sortRows,
} from '@/components/OrgTaxHistoryTable';

// ч.745: користувацькі податкові рядки без назви (порожні «додати рядок») не
// відправляємо — бек вимагає name.
function cleanTaxRows<T extends {
  tax_accounts_extra?: OrganizationCreate['tax_accounts_extra'];
  activity_places?: OrganizationCreate['activity_places'];
  tax_history?: OrganizationCreate['tax_history'];
}>(p: T): T {
  const out = { ...p };
  if (out.tax_accounts_extra) out.tax_accounts_extra = out.tax_accounts_extra.filter((r) => r.name.trim());
  // ч.806: місця діяльності без назви — не відправляємо.
  if (out.activity_places) out.activity_places = out.activity_places.filter((r) => r.name.trim());
  // ч.1383: рядки податкового статусу — лише поля, які приймає сервер (без «джерела»).
  if (out.tax_history) {
    out.tax_history = out.tax_history.map(({ id, valid_from, tax_system, is_vat_payer, vat_number }) => (
      { id: id ?? null, valid_from, tax_system, is_vat_payer, vat_number }));
  }
  return out;
}

/** Сьогоднішня дата за київським часом, YYYY-MM-DD (ч.1376: дата початку обліку за журналом). */
function kyivToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Kyiv' }).format(new Date());
}

const EMPTY: OrganizationCreate = {
  // Auto-code (2026-06-02): null → backend генерує "ORG-N".
  code: null,
  name: '',
  full_name: '',
  entity_type: 'LEGAL_ENTITY',
  edrpou: null,
  inn: null,
  is_vat_payer: null,  // ч.1381: обовʼязковий вибір «так / ні»
  vat_number: null,
  vat_considered_default: true,
  prices_include_vat_default: true,
  vat_auto_default: true,
  legal_address: null,
  actual_address: null,
  phone: null,
  email: null,
  director_name: null,
  chief_accountant: null,
  director_inn: null,
  chief_accountant_inn: null,
  chief_accountant_signs_reports: false,
  sti_region: null,
  sti_district: null,
  sti_name: null,
  sti_code: null,
  cost_method: 'WAVG',
  production_costing_mode: 'SIMPLE',
  tax_system: 'GENERAL',
  // ч.1118: 12 місяців — за Порядком №1266 (п.25); 24 лишено як свідомий вибір.
  maternity_calc_period_months: 12,
  fx_revaluation_period: 'QUARTERLY',
  base_currency_code: 'UAH',
  business_template_id: null,
  use_double_entry: true,
  use_chart_of_accounts: true,
  use_recipes: false,
  is_diia_city_resident: false,
  disability_norm_2pct: false,
  fin_report_form: 'MICRO',
  profit_tax_period: 'AUTO',
  dps_store_docs: false,
  use_retail: false,
  use_directions: false,
  use_returnable_packaging: false,
  tax_pdfo_recipient_name: null,
  tax_pdfo_recipient_edrpou: null,
  tax_pdfo_recipient_iban: null,
  tax_wz_recipient_name: null,
  tax_wz_recipient_edrpou: null,
  tax_wz_recipient_iban: null,
  tax_esv_recipient_name: null,
  tax_esv_recipient_edrpou: null,
  tax_esv_recipient_iban: null,
  tax_accounts_extra: [],
  activity_places: [],
  tax_purpose_templates: {},
  registration_date: null,
  kved_main: null,
  kved_extra: [],
  // ORG-EDITOR.1 (2026-06-04): ОПФГ + КОАТУУ.
  opfg_code: null,
  opfg_name: null,
  own_name: null,
  opf_short: null,
  koatuu_code: null,
};

// ORG-EDITOR.1 (2026-06-04): таби bottom-block у формі організації.
type OrgTab = 'codes' | 'contacts' | 'bank' | 'warehouses' | 'cash-registers' | 'tax' | 'policy' | 'edo';

/**
 * ORG-EDITOR.3 hotfix (2026-06-04): форматує помилку save mutation
 * у читабельний рядок для inline error block у Drawer'і.
 *
 * Backend може повернути:
 * - 422 з detail = Array<{loc, msg, type}> (Pydantic validation)
 * - 4xx/5xx з detail = string (HTTPException)
 * - Network error без response
 */
function formatSaveError(err: unknown): string {
  if (!err) return 'Невідома помилка';
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      | undefined;
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((e) => {
          const field = Array.isArray(e.loc) ? e.loc.slice(1).join('.') : '';
          return field ? `${field}: ${e.msg}` : e.msg ?? '';
        })
        .filter(Boolean)
        .join('; ');
    }
    return err.message;
  }
  return String(err);
}

// Рішення власника 2026-06-02: checksum прибрана, лише формат.
// Прецедент: ЄДРПОУ 34927739, 40075815 (Розетка), 39119549 (Нова Пошта)
// — реальні юр.особи, але не проходять стандартний алгоритм Держстату.
function isValidEdrpou(v: string): boolean {
  return /^\d{8}$/.test(v);
}

function isValidRnokpp(v: string): boolean {
  return /^\d{10}$/.test(v);
}

function isValidKved(v: string): boolean {
  return /^\d{2}\.\d{1,2}$/.test(v);
}

/**
 * ч.1235: ПІБ відповідальної особи ФОП — сам підприємець. Поки картку не збережено,
 * показуємо те, що запише сервер: власну назву, а в старій картці без неї — назву без
 * «Фізична особа-підприємець» / «ФОП» (та сама логіка, що `core.fop_person_name`).
 */
export function fopHeadName(f: { own_name?: string | null; full_name?: string | null;
  name?: string | null; director_name?: string | null }): string {
  const src = (f.own_name ?? '').trim() || (f.full_name ?? '').trim() || (f.name ?? '').trim();
  const person = src.replace(/^\s*(фізична\s+особа\s*[-–—]?\s*підприємець|фоп)\s+/i, '').trim();
  return person || (f.director_name ?? '');
}

export default function OrganizationCardPage() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const closeCard = () => navigate('/organizations');
  // Орг з того самого query, що на списку (їх мало, кешовано).
  const list = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const editing: Organization | 'new' | null = isNew
    ? 'new'
    : (list.data?.items.find((o) => o.id === id) ?? null);
  // Заповнення форми ПІСЛЯ завантаження орг (раз на id). ВАЖЛИВО: через
  // useEffect, НЕ інлайн у рендері — startEdit викликає setForm/setActiveTab,
  // чиї useState-байндінги оголошені НИЖЧЕ (інлайн-виклик = TDZ,
  // «Cannot access uninitialized variable»).
  const [loadedId, setLoadedId] = useState<string | null>(null);
  useEffect(() => {
    if (editing && editing !== 'new' && loadedId !== editing.id) {
      setLoadedId(editing.id);
      startEdit(editing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, loadedId]);
  // 2026-08-13 (рішення власника): вкладка «Печатка та підпис» на картці.
  const [orgTab, setOrgTab] = useState<'main' | 'stamps' | OrgTab>('main');
  const [form, setForm] = useState<OrganizationCreate>(EMPTY);
  // ч.1059: чернетка незбережених правок картки (як у документах) — живе
  // лише у браузері, при поверненні пропонує відновити. Вкладки «Банк»,
  // «Склади», «Каси», «Печатка» мають власні мутації, які картку НЕ
  // зберігають, — тому чернетку стираємо лише явним markSaved.
  // ч.1060: ввід усередині вкладок (рядок нового банк-рахунку, форма складу/
  // каси, відкладена галочка «Контроль підписання») — у СЛОТАХ картки: не
  // губиться при перемиканні вкладок і входить у чернетку разом з формою.
  const [slots, setSlots] = useState<DraftSlots>({});
  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  const draftValue = useMemo(() => ({ form, slots }), [form, slots]);
  const restoreDraft = useCallback((raw: unknown) => {
    // Чернетка ч.1059 зберігала лише форму — розбираємо обидва формати.
    const v = withLegacyForm<{ form: OrganizationCreate; slots?: DraftSlots }>(raw);
    setForm({ ...EMPTY, ...v.form });
    setSlots(v.slots ?? {});
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, {
    ready: isNew || (!!editing && editing !== 'new' && loadedId === editing.id),
    subject: 'цієї картки',
    clearOnAnyMutation: false,
  });
  // ORG-EDITOR.1 (2026-06-04): таби нижнього блоку.
  // Одна верхня полоса вкладок — ОДНАКОВО для нової і збереженої орг.
  const showTab = (k: OrgTab) => orgTab === k;
  // Права per-org для вкладок Склади/Каси (паттерн view=wide, edit=narrow):
  // список видно завжди (RLS), кнопки create/edit/delete — лише з refs.warehouses.edit.
  const { permissionsByOrg } = useAllPermissionsByOrg();
  const orgIdForRefs = editing && editing !== 'new' ? editing.id : '';
  // ч.1376: облік лише за журналом доходів — ФОП 1–3 груп без ПДВ; дата початку фіксується
  // після першого збереження з галочкою.
  const journalEligible = form.entity_type === 'FOP'
    && ['FOP_1', 'FOP_2', 'FOP_3_5'].includes(form.tax_system) && !form.is_vat_payer;
  const journalSavedOn = Boolean(editing && editing !== 'new' && (editing as Organization).income_journal_only);
  const canManageRefs = orgIdForRefs
    ? (permissionsByOrg[orgIdForRefs] ?? []).includes('refs.warehouses.edit')
    : false;
  const refsCantReason = 'Немає права створювати/змінювати склади/каси у цій юрособі '
    + '(потрібен refs.warehouses.edit)';
  // Inline bank-account (при створенні нового org) — оптіональні поля.
  // При зберіганні зашлемо okремий POST /refs/bank-accounts якщо iban не порожній.
  // is_main: true за default бо це перший рахунок (логічно).
  const [newBankIban, setNewBankIban] = useState('');
  const [newBankCurrencyId, setNewBankCurrencyId] = useState('');
  const [newBankAccountName, setNewBankAccountName] = useState('');
  const [newBankIsMain, setNewBankIsMain] = useState(true);
  const [resolvingOfficials, setResolvingOfficials] = useState(false);
  // Пошук ДПІ (вкладка «Коди»): юзер обирає свою податкову зі списку —
  // проставляються код ДПІ (C_STI_ORIG), область (C_REG) і назва (HSTI).
  const [stiSearch, setStiSearch] = useState('');
  const [stiOpen, setStiOpen] = useState(false);
  const stiQ = useQuery({
    queryKey: ['tax-offices', stiSearch],
    queryFn: () => taxOffices.list({ q: stiSearch || undefined, limit: 30 }),
    enabled: stiOpen,
    staleTime: 1000 * 60 * 30,
  });

  async function onResolveOfficials() {
    if (!orgIdForRefs) return;
    setResolvingOfficials(true);
    try {
      const res = await resolveOrgOfficials(orgIdForRefs);
      const u = res.updated;
      if (Object.keys(u).length > 0) {
        // ч.1179: null у відповіді = знято (переведений/звільнений) — поле очищаємо.
        setForm((f) => ({
          ...f,
          director_name: 'director_name' in u ? u.director_name : f.director_name,
          director_inn: 'director_inn' in u ? u.director_inn : f.director_inn,
          chief_accountant: 'chief_accountant' in u ? u.chief_accountant : f.chief_accountant,
          chief_accountant_inn: 'chief_accountant_inn' in u ? u.chief_accountant_inn : f.chief_accountant_inn,
          chief_accountant_signs_reports: 'chief_accountant' in u && u.chief_accountant === null
            ? false : f.chief_accountant_signs_reports,
        }));
        alert('Підтягнуто з наказів: '
          + [u.director_name && `керівник ${u.director_name}`,
             u.chief_accountant && `головбух ${u.chief_accountant}`,
             'director_name' in u && u.director_name === null && 'керівника знято (переведений або звільнений)',
             'chief_accountant' in u && u.chief_accountant === null && 'головбуха знято (переведений або звільнений)']
            .filter(Boolean).join(', '));
      } else {
        alert('У проведених наказах не знайдено керівника/головного бухгалтера '
          + `(перевірено ${res.found_orders} наказів). Впишіть вручну.`);
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail ?? 'Помилка');
    } finally {
      setResolvingOfficials(false);
    }
  }

  const templatesQ = useQuery({
    queryKey: ['admin-templates'],
    queryFn: () => businessTemplates.list(),
  });
  // Currencies для inline bank-account UI.
  const currenciesQ = useQuery({
    queryKey: ['currencies'],
    queryFn: () => refs.currencies(),
  });
  // ORG-EDITOR.2 (2026-06-04): banks lookup з NBU для auto-bank-name.
  const banksQ = useQuery({
    queryKey: ['banks'],
    queryFn: () => refs.banks(),
    staleTime: 1000 * 60 * 60, // 1h — банки рідко змінюються
  });
  // Bank accounts existing org. NULL при створенні нового — refetch skip.
  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', editing && editing !== 'new' ? editing.id : null],
    queryFn: () => refs.bankAccounts(
      editing && editing !== 'new' ? editing.id : undefined,
    ),
    enabled: editing !== null && editing !== 'new',
  });
  const create = useMutation({
    mutationFn: async (p: OrganizationCreate) => {
      const org = await orgs.create(cleanTaxRows(p));
      // ORG-EDITOR.1 (2026-06-04): якщо при створенні вказано IBAN —
      // створюємо bank-account inline.
      if (newBankIban.trim() && newBankCurrencyId) {
        try {
          await refs.createBankAccount({
            organization_id: org.id,
            iban: newBankIban.replace(/\s+/g, '').toUpperCase(),
            currency_id: newBankCurrencyId,
            account_name: newBankAccountName.trim() || null,
            is_main: newBankIsMain,
          });
        } catch (e) {
          // Soft-fail: organization створена, але bank-account не вдалося.
          // Користувач зможе додати його руками при редагуванні.
          console.warn('Inline bank-account creation failed:', e);
        }
      }
      return org;
    },
    onSuccess: () => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['orgs'] });
      // ч.1088: меню (ПДВ / Роздріб / тара) рахується з ['orgs-for-modules'].
      qc.invalidateQueries({ queryKey: ['orgs-for-modules'] });
      closeCard();
      // Скидаємо inline bank state.
      setNewBankIban(''); setNewBankCurrencyId(''); setNewBankAccountName('');
    },
  });
  // 2026-08-19: галочка контролю підписання зберігається РАЗОМ з карткою
  // (кнопкою «зберегти»), а не миттєво. ч.1060: значення — зі слота картки,
  // тож зберігається з будь-якої вкладки (раніше ref.flush() бачив його лише,
  // поки вкладка «Облікова політика» була відкрита).
  const update = useMutation({
    mutationFn: async ({ id, p }: { id: string; p: Partial<OrganizationCreate> }) => {
      await saveSignControlPending(qc, id,
        slotsRef.current[SIGN_CONTROL_PENDING_SLOT] as boolean | null | undefined);
      return orgs.update(id, cleanTaxRows(p));
    },
    onSuccess: () => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: ['orgs-for-modules'] });   // ч.1088: меню
      closeCard();
    },
  });
  // ORG-EDITOR.1 (2026-06-04): mutations для bank-accounts при редагуванні.
  const addBankAccount = useMutation({
    mutationFn: (p: { iban: string; currency_id: string; account_name: string | null }) => {
      if (!editing || editing === 'new') throw new Error('No org selected');
      return refs.createBankAccount({
        organization_id: editing.id,
        iban: p.iban.replace(/\s+/g, '').toUpperCase(),
        currency_id: p.currency_id,
        account_name: p.account_name,
        is_main: bankAccountsQ.data?.length === 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
  const removeBankAccount = useMutation({
    mutationFn: (id: string) => refs.removeBankAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
  // ORG-EDITOR.2 (2026-06-04): set main — backend auto-unset попередній main.
  const setMainBankAccount = useMutation({
    mutationFn: (id: string) => refs.updateBankAccount(id, { is_main: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });

  function startEdit(o: Organization) {
    // ч.746: legacy-картка без own_name — вивести власну назву/оргформу з
    // наявних назв (лише у форму; збережеться після «зберегти»).
    const derived = o.own_name ? null : deriveOwnName({
      name: o.name, full_name: o.full_name, opfg_code: o.opfg_code, opfg_name: o.opfg_name });
    setForm({
      code: o.code, name: o.name, full_name: o.full_name,
      entity_type: o.entity_type,
      edrpou: o.edrpou, inn: o.inn,
      is_vat_payer: o.is_vat_payer, vat_number: o.vat_number,
      // ч.1383: податковий статус за датами (поля вище — рядок, що діє сьогодні).
      tax_history: sortRows(o.tax_history ?? []),
      vat_considered_default: o.vat_considered_default ?? true,
      prices_include_vat_default: o.prices_include_vat_default ?? true,
      vat_auto_default: o.vat_auto_default ?? true,
      legal_address: o.legal_address,
      actual_address: o.actual_address,
      phone: o.phone, email: o.email,
      director_name: o.director_name,
      chief_accountant: o.chief_accountant,
      director_inn: o.director_inn ?? null,
      chief_accountant_inn: o.chief_accountant_inn ?? null,
      chief_accountant_signs_reports: o.chief_accountant_signs_reports ?? false,
      sti_region: o.sti_region ?? null,
      sti_district: o.sti_district ?? null,
      sti_name: o.sti_name ?? null,
      sti_code: o.sti_code ?? null,
      cost_method: o.cost_method, tax_system: o.tax_system,
      production_costing_mode: o.production_costing_mode ?? 'SIMPLE',
      // TD-B-P5.2.3 (2026-06-01).
      maternity_calc_period_months: o.maternity_calc_period_months ?? 12,
      fx_revaluation_period: o.fx_revaluation_period ?? 'QUARTERLY',
      base_currency_code: o.base_currency_code,
      business_template_id: o.business_template_id,
      use_double_entry: o.use_double_entry,
      use_chart_of_accounts: o.use_chart_of_accounts,
      use_recipes: o.use_recipes,
      is_diia_city_resident: o.is_diia_city_resident,
      disability_norm_2pct: o.disability_norm_2pct ?? false,
      fin_report_form: o.fin_report_form ?? 'MICRO',
      profit_tax_period: o.profit_tax_period ?? 'AUTO',
      dps_store_docs: o.dps_store_docs,
      use_retail: o.use_retail,
      use_directions: o.use_directions,
      use_returnable_packaging: o.use_returnable_packaging,
      income_journal_only: o.income_journal_only ?? false,
      income_journal_since: o.income_journal_since ?? null,
      tax_pdfo_recipient_name: o.tax_pdfo_recipient_name,
      tax_pdfo_recipient_edrpou: o.tax_pdfo_recipient_edrpou,
      tax_pdfo_recipient_iban: o.tax_pdfo_recipient_iban,
      tax_wz_recipient_name: o.tax_wz_recipient_name,
      tax_wz_recipient_edrpou: o.tax_wz_recipient_edrpou,
      tax_wz_recipient_iban: o.tax_wz_recipient_iban,
      tax_esv_recipient_name: o.tax_esv_recipient_name,
      tax_esv_recipient_edrpou: o.tax_esv_recipient_edrpou,
      tax_esv_recipient_iban: o.tax_esv_recipient_iban,
      tax_accounts_extra: o.tax_accounts_extra ?? [],
      activity_places: o.activity_places ?? [],
      tax_purpose_templates: o.tax_purpose_templates ?? {},
      registration_date: o.registration_date,
      kved_main: o.kved_main,
      kved_extra: o.kved_extra,
      // ORG-EDITOR.1 (2026-06-04): ОПФГ + КОАТУУ.
      opfg_code: o.opfg_code ?? derived?.opfg_code ?? null,
      opfg_name: o.opfg_name ?? derived?.opfg_name ?? null,
      own_name: o.own_name ?? derived?.own_name ?? null,
      opf_short: o.opf_short ?? derived?.opf_short ?? null,
      koatuu_code: o.koatuu_code,
    });
  }
  // ч.1381: чого бракує у статусі ПДВ після натискання «зберегти».
  const [vatError, setVatError] = useState<string | null>(null);
  // ч.1384 (рішення власника): поля статусу заповнюють лише при створенні. У збереженої
  // організації вони показують рядок «Податковий статус за датами», що діє сьогодні, і не
  // редагуються — статус змінюють рядком у таблиці.
  // Організація без жодного рядка (статус ще не зафіксовано) — поля задають перший рядок.
  const statusLocked = editing !== 'new' && (editing?.tax_history?.length ?? 0) > 0;
  const lockedNote = statusLocked
    ? 'Змінюється в таблиці «Податковий статус за датами»: додайте рядок з дати' : undefined;
  const lockedBefore = useMemo(
    () => (editing && editing !== 'new' ? lockedBeforeOf(editing.tax_history ?? []) : null),
    [editing]);
  function setStatus(p: Partial<Pick<OrganizationCreate, 'tax_system' | 'is_vat_payer' | 'vat_number'>>) {
    if (statusLocked) return;
    setForm({ ...form, ...p });
  }
  function setHistory(rows: OrgTaxStatus[]) {
    const cur = rows[currentIndex(rows)];
    setForm({ ...form, tax_history: rows,
      ...(cur ? { tax_system: cur.tax_system, is_vat_payer: cur.is_vat_payer, vat_number: cur.vat_number } : {}) });
  }
  // Confirmation modal стан для перемикання шаблону зі звуженням модулів.
  const [templateSwitchConfirm, setTemplateSwitchConfirm] = useState<{
    open: boolean;
    removedModules: string[];
    affected: AffectedDocs | null;
    isLoading: boolean;
  }>({ open: false, removedModules: [], affected: null, isLoading: false });

  async function save() {
    // ч.1060: недодані рядки у вкладках (новий банк-рахунок, склад, каса) —
    // картка після збереження закривається, тож питаємо, а не губимо мовчки.
    if (!(await confirmPendingRows(slots, confirm))) return;
    // ч.1381: статус платника ПДВ — обовʼязковий вибір; платник — з ІПН. ч.1384: у збереженої
    // організації статус задає таблиця — перевіряємо її нові рядки.
    const vatProblem = statusLocked
      ? historyProblem(form.tax_history ?? []) : vatPayerProblem(form);
    setVatError(vatProblem);
    if (vatProblem) return;
    if (editing === 'new') {
      create.mutate(form);
      return;
    }
    if (!editing) return;
    const oldTplId = (editing as Organization).business_template_id;
    const newTplId = form.business_template_id;

    // Якщо template НЕ змінився, або переходимо з/у null — звичайний save.
    if (oldTplId === newTplId || !oldTplId || !newTplId) {
      update.mutate({ id: editing.id, p: form });
      return;
    }

    // Перевірити чи новий шаблон ВУЖЧИЙ за старий за модулями.
    const oldTpl = templatesQ.data?.find((t) => t.id === oldTplId);
    const newTpl = templatesQ.data?.find((t) => t.id === newTplId);
    if (!oldTpl || !newTpl) {
      update.mutate({ id: editing.id, p: form });
      return;
    }
    const removed = oldTpl.enabled_modules.filter(
      (m) => !newTpl.enabled_modules.includes(m),
    );
    if (removed.length === 0) {
      update.mutate({ id: editing.id, p: form });
      return;
    }

    // Показати confirmation з підрахунком документів цього org'а.
    setTemplateSwitchConfirm({
      open: true, removedModules: removed, affected: null, isLoading: true,
    });
    try {
      const data = await businessTemplates.affectedDocs(
        newTplId, removed, editing.id,
      );
      setTemplateSwitchConfirm((s) => ({ ...s, affected: data, isLoading: false }));
    } catch {
      // На помилку affected-docs — показати модалку з 0 (failed-soft).
      setTemplateSwitchConfirm((s) => ({
        ...s, affected: { total: 0, by_module: {} }, isLoading: false,
      }));
    }
  }

  function confirmTemplateSwitch() {
    if (!editing || editing === 'new') return;
    setTemplateSwitchConfirm((s) => ({ ...s, open: false }));
    update.mutate({ id: editing.id, p: form });
  }

  if (!isNew && !editing && list.isLoading) {
    return <div className="card"><p>Завантаження…</p></div>;
  }
  if (!isNew && !editing && !list.isLoading) {
    return (
      <div className="card">
        <p>Організацію не знайдено.</p>
        <button onClick={closeCard}>до списку</button>
      </div>
    );
  }

  // ч.758: лічильники незаповненого на вкладках «Коди» (КАТОТТГ) і
  // «Податкові рахунки» (код ДПІ + 3 податки з ЗП) — червоний надрядковий індекс.
  const codesMissing = form.koatuu_code ? 0 : 1;
  const taxMissing = (form.sti_code ? 0 : 1)
    + (form.tax_pdfo_recipient_iban ? 0 : 1)
    + (form.tax_wz_recipient_iban ? 0 : 1)
    + (form.tax_esv_recipient_iban ? 0 : 1);
  const missingBadge = (n: number) => (n > 0 ? (
    <sup style={{ color: '#d33', fontWeight: 700, marginLeft: 3, fontSize: '0.7em' }}
      title="Не заповнено полів">{n}</sup>
  ) : null);

  return (
    <DraftSlotsProvider slots={slots} setSlots={setSlots}>
      <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          {editing === 'new' ? t('org.new') : (editing as Organization | null)?.name ?? ''}
        </h2>
        {/* ч.642: канон DocumentToolbar (плашка дій + інфо-ряд у рядку назви). */}
        <DocumentToolbar
          onBackToList={closeCard}
          onSave={save}
          savePending={create.isPending || update.isPending}
          postable={false}
          isNew={editing === 'new'}
          // ч.1105 (рішення власника): організацію видаляють лише у розділі
          // «Обліковий запис» → «Видалити організацію» (пароль, назва, ключ КЕП).
          deletable={false}
        />
      </div>
        {/* Inline помилка save — щоб не покладатись лише на глобальний toast.
            (2026-06-04 ORG-EDITOR.3 hotfix: власник скаржився «не сохраняет
            рахунок» — токен швидко зникав і не зрозуміло було що падає). */}
        {vatError && (
          <div role="alert" style={{
            background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', padding: '0.75rem',
            borderRadius: 6, marginBottom: '0.75rem', color: '#b00',
          }}>
            <strong>Не вдалось зберегти:</strong> вкажіть {vatError}.
          </div>
        )}
        {(create.isError || update.isError) && (
          <div style={{
            background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', padding: '0.75rem',
            borderRadius: 6, marginBottom: '0.75rem', color: '#b00',
          }}>
            <strong>❌ Не вдалось зберегти:</strong>
            <div style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
              {formatSaveError(create.error ?? update.error)}
            </div>
          </div>
        )}

        {/* Вкладки картки (2026-08-17, рішення власника): УСІ вкладки однією
            полосою вгорі — ОДНАКОВО для нової і збереженої орг (2026-08-17
            уточнення: форма створення мусить виглядати так само). */}
        {(editing === 'new' || editing) && (
          <div className="seg-tabs" style={{ marginBottom: '0.75rem' }}>
            <button type="button" className={orgTab === 'main' ? 'active' : ''}
              onClick={() => setOrgTab('main')}>Основне</button>
            <button type="button" className={orgTab === 'policy' ? 'active' : ''}
              onClick={() => setOrgTab('policy')}>Облікова політика</button>
            <button type="button" className={orgTab === 'codes' ? 'active' : ''}
              onClick={() => setOrgTab('codes')}>Коди{missingBadge(codesMissing)}</button>
            <button type="button" className={orgTab === 'contacts' ? 'active' : ''}
              onClick={() => setOrgTab('contacts')}>Контакти</button>
            <button type="button" className={orgTab === 'bank' ? 'active' : ''}
              onClick={() => setOrgTab('bank')}>Банк</button>
            <button type="button" className={orgTab === 'warehouses' ? 'active' : ''}
              onClick={() => setOrgTab('warehouses')}>Склади</button>
            <button type="button" className={orgTab === 'cash-registers' ? 'active' : ''}
              onClick={() => setOrgTab('cash-registers')}>Каси</button>
            <button type="button" className={orgTab === 'tax' ? 'active' : ''}
              onClick={() => setOrgTab('tax')}>Податкові рахунки{missingBadge(taxMissing)}</button>
            <button type="button" className={orgTab === 'edo' ? 'active' : ''}
              onClick={() => setOrgTab('edo')}>ЕДО</button>
            <button type="button" className={orgTab === 'stamps' ? 'active' : ''}
              onClick={() => setOrgTab('stamps')}>Печатка та підпис</button>
          </div>
        )}
        {orgTab === 'main' && (<>
        {/* === Основне (перебудова 2026-08-17, рішення власника) ===
            Рядки через .doc-row: поля СТИСКАЮТЬСЯ, а не перестрибують
            при згортанні/розгортанні лівого субменю. */}
        <div className="doc-row">
          {/* ч.746: Оргформа (пошук) → Назва (без оргформи) → Скорочено;
              назви компілюються, entity_type — з оргформи. */}
          <OrgNameFields form={form} setForm={setForm} />
          <div className="field" style={{ width: 130 }}>
            <label>{t('org.code')}</label>
            <input
              value={form.code ?? ''}
              onChange={(e) => setForm({ ...form, code: e.target.value || null })}
              placeholder="ORG-1 (auto)"
            />
          </div>
        </div>

        <OrgNamePreview form={form} />

        {/* ЄДРПОУ/РНОКПП (+дата реєстрації ФОП) + система оподаткування +
            галка ПДВ + номер свідоцтва — ОДИН рядок (рішення власника
            2026-08-17). .doc-row: фіксовані ширини + стиснення без переносу. */}
        <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
          {form.entity_type === 'LEGAL_ENTITY' ? (
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>{t('org.edrpou')} * <small style={{ color: 'var(--muted)' }}>(8 цифр)</small></label>
              <input
                value={form.edrpou ?? ''}
                maxLength={8}
                placeholder="12345678"
                onChange={(e) => setForm({
                  ...form, edrpou: e.target.value.replace(/\D/g, '') || null,
                })}
              />
              {form.edrpou && (
                <small style={{ color: isValidEdrpou(form.edrpou) ? 'green' : '#d33' }}>
                  {isValidEdrpou(form.edrpou)
                    ? '✓ Контрольна сума'
                    : form.edrpou.length === 8
                      ? '✗ Невірна контрольна сума'
                      : `✗ 8 цифр (зараз ${form.edrpou.length})`
                  }
                </small>
              )}
            </div>
          ) : (<>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>РНОКПП * <small style={{ color: 'var(--muted)' }}>(10 цифр)</small></label>
              <input
                value={form.inn ?? ''}
                maxLength={10}
                placeholder="1234567890"
                onChange={(e) => setForm({
                  ...form, inn: e.target.value.replace(/\D/g, '') || null,
                })}
              />
              {form.inn && (
                <small style={{ color: isValidRnokpp(form.inn) ? 'green' : '#d33' }}>
                  {isValidRnokpp(form.inn)
                    ? '✓ Контрольна сума'
                    : form.inn.length === 10
                      ? '✗ Невірна контрольна сума'
                      : `✗ 10 цифр (зараз ${form.inn.length})`
                  }
                </small>
              )}
            </div>
            {/* Дата держреєстрації ФОП: НЕ прикраса — три сервіси
                (ЄСВ «за себе», єдиний податок, ФОП ЗСО) блокують розрахунок
                за періоди ДО неї; backend вимагає її (Норма #20). */}
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label title="Використовується у розрахунках ЄСВ/єдиного податку — періоди до дати реєстрації не рахуються">
                Дата реєстрації *
              </label>
              <DatePopover
                value={form.registration_date ?? ''}
                onChange={(v) => setForm({ ...form, registration_date: v || null })}
              />
            </div>
          </>)}
          <div className="field" style={{ gridColumn: form.entity_type === 'LEGAL_ENTITY' ? 'span 5' : 'span 3' }}>
            <label>{t('org.tax_system')} *</label>
            <select
              value={form.tax_system}
              disabled={statusLocked}
              title={lockedNote}
              onChange={(e) => {
                const newTs = e.target.value as OrganizationCreate['tax_system'];
                // Smart cascade: FOP-групи з фіксованим ПДВ-статусом; з такої групи на
                // систему з вибором — знову «оберіть» (ч.1381).
                const vat = vatAfterTaxSystem(form.tax_system, newTs, form.is_vat_payer);
                setStatus({
                  tax_system: newTs,
                  is_vat_payer: vat,
                  // Неплатник — без ІПН платника ПДВ.
                  vat_number: vat === false ? null : form.vat_number,
                });
              }}
            >
              {form.entity_type === 'LEGAL_ENTITY' ? (
                <>
                  <option value="GENERAL">Загальна (податок на прибуток 18%)</option>
                  <option value="SIMPLIFIED_3">Спрощена 3 група (ТОВ)</option>
                  <option value="SIMPLIFIED_4">Спрощена 4 (с/г ТОВ)</option>
                </>
              ) : form.entity_type === 'INDIVIDUAL' ? (
                <option value="NONE">Облік не ведеться (лише ЕДО)</option>
              ) : (
                <>
                  <option value="FOP_GENERAL">ФОП на загальній (ст. 177 ПКУ)</option>
                  <option value="FOP_1">ФОП 1 група (єд. податок)</option>
                  <option value="FOP_2">ФОП 2 група (єд. податок)</option>
                  <option value="FOP_3_3">ФОП 3 група 3% (платник ПДВ)</option>
                  <option value="FOP_3_5">ФОП 3 група 5% (неплатник ПДВ)</option>
                </>
              )}
            </select>
          </div>
          {(() => {
            const vatFixed = vatFixedFor(form.tax_system);
            // ФОП: поле показуємо ЛИШЕ коли ПДВ можливий (рішення власника).
            if (form.entity_type === 'FOP' && vatFixed === false) return null;
            return (
              <div className="field" style={{ gridColumn: 'span 2' }}>
                <VatPayerSelect value={form.is_vat_payer} fixed={vatFixed} locked={lockedNote}
                  invalid={vatError !== null && form.is_vat_payer === null}
                  onChange={(v) => {
                    setVatError(null);
                    setStatus({ is_vat_payer: v, vat_number: v ? form.vat_number : null });
                  }} />
              </div>
            );
          })()}
          {form.is_vat_payer && (
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>{t('org.vat_number')} * <small style={{ color: 'var(--muted)' }}>(9-12 цифр)</small></label>
              <input
                aria-invalid={(vatError !== null && !(form.vat_number ?? '').trim()) || undefined}
                value={form.vat_number ?? ''}
                disabled={statusLocked}
                title={lockedNote}
                maxLength={12}
                placeholder="123456789"
                onChange={(e) => setStatus({
                  vat_number: e.target.value.replace(/\D/g, '') || null,
                })}
              />
            </div>
          )}
        </div>
        {form.is_vat_payer && (
          /* ч.1374: умовчання ознак ПДВ нових документів — як «Ціни та валюта → Податки» в 1С;
             у кожному документі їх можна змінити кнопкою «ціна» в таблиці рядків. */
          <div className="doc-grid" style={{ marginTop: '0.25rem' }}>
            {([
              ['vat_considered_default', 'Враховувати ПДВ',
                'Нові документи з ПДВ: у рядках є ставка і сума ПДВ. Вимкнено — документи без ПДВ (на друку «Ціна», «Сума»).'],
              ['prices_include_vat_default', 'Сума включає ПДВ',
                'Ціна рядка вже з ПДВ — сума без ПДВ виділяється з неї; на друку «Ціна з ПДВ» і «У тому числі ПДВ».'],
              ['vat_auto_default', 'Автоматичний розрахунок ПДВ',
                'Суму ПДВ рядка рахує система зі ставки. Вимкнено — суму ПДВ можна вписати вручну.'],
            ] as const).map(([key, label, hint]) => (
              <div className="field" key={key} style={{ gridColumn: 'span 4' }}>
                <label style={{ whiteSpace: 'nowrap' }} title={hint}>
                  <input type="checkbox"
                    checked={form[key] ?? true}
                    onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                    style={{ width: 'auto', marginRight: '0.5rem' }} />
                  {label}
                  <small style={{ display: 'block', color: 'var(--muted)', whiteSpace: 'normal' }}>{hint}</small>
                </label>
              </div>
            ))}
          </div>
        )}
        {statusLocked && (
          /* ч.1384: таблиця — під галочками ПДВ (рішення власника). */
          <OrgTaxHistoryTable rows={form.tax_history ?? []} entityType={form.entity_type}
            lockedBefore={lockedBefore}
            onChange={(rows) => { setVatError(null); setHistory(rows); }} />
        )}
        {form.entity_type === 'FOP' && form.tax_system === 'FOP_3_3' && !form.is_vat_payer && (
          <small style={{ color: '#d33' }}>✗ ФОП 3 група 3% — обовʼязковий платник ПДВ</small>
        )}
        {form.entity_type === 'FOP' && form.tax_system === 'FOP_3_5' && form.is_vat_payer && (
          <small style={{ color: '#d33' }}>✗ ФОП 3 група 5% — не може бути платником ПДВ</small>
        )}
        {form.entity_type === 'FOP' && (form.tax_system === 'FOP_1' || form.tax_system === 'FOP_2') && form.is_vat_payer && (
          <small style={{ color: '#d33' }}>✗ ФОП 1/2 групи не можуть бути платниками ПДВ</small>
        )}

        {/* Бізнес-шаблон (код організації — у першому рядку). */}
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Бізнес-шаблон</label>
            <select
              value={form.business_template_id ?? ''}
              onChange={(e) => setForm({ ...form, business_template_id: e.target.value || null })}
            >
              <option value="">— Без шаблону —</option>
              {templatesQ.data?.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
              ))}
            </select>
            {editing === 'new' && (
              <small style={{ color: 'var(--muted)' }}>
                При створенні застосує налаштування облікової політики шаблону.
              </small>
            )}
          </div>
        </div>
        </>)}
        {/* Облікова політика (рішення власника 2026-08-23): усі облікові
            налаштування юрособи — окремою вкладкою. */}
        {orgTab === 'policy' && (<>
        {/* 0431: резидент Дія.Сіті — зарплатні податки ПДФО 5% / ВЗ 5% /
            ЄСВ 22% (8.41% інвалід) від МЗП + ознака 08.5 у Податковому
            розрахунку. Прибуток — звичайний облік (рішення власника). */}
        {form.entity_type === 'LEGAL_ENTITY' && (
          <label style={{ cursor: 'pointer', display: 'block', margin: '0.35rem 0' }}
            title="ПДФО 5%, ВЗ 5%, ЄСВ 22% від мінімальної зарплати (пільга інваліда 8.41% зберігається). У Податковому розрахунку проставляється ознака «Резидент Дія Сіті». Податок на прибуток — звичайний облік.">
            <input
              type="checkbox"
              checked={form.is_diia_city_resident ?? false}
              onChange={(e) => setForm({ ...form, is_diia_city_resident: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Резидент Дія.Сіті
          </label>
        )}
        {/* ч.1366: норматив працевлаштування осіб з інвалідністю 2 % (ч.1208) — раніше лише в
            майстрі створення; для юрособи і ФОП (звіт про внесок подає будь-який роботодавець). */}
        <label style={{ cursor: 'pointer', display: 'block', margin: '0.35rem 0' }}
          title="Заклад охорони здоров’я, реабілітаційний заклад, надавач соціальних послуг або організація, основний вид діяльності якої — реабілітація осіб з інвалідністю, їх навчання чи догляд за ними. У звіті про внесок норматив рахується як 2 % середньооблікової кількості, категорія платника — 093.">
          <input
            type="checkbox"
            checked={form.disability_norm_2pct ?? false}
            onChange={(e) => setForm({ ...form, disability_norm_2pct: e.target.checked })}
            style={{ width: 'auto', marginRight: '0.4rem' }}
          />
          Норматив працевлаштування осіб з інвалідністю — 2 % (охорона здоров’я, реабілітація, соціальні послуги)
        </label>

        {/* ч.1376 (рішення власника): ФОП-єдинник без ПДВ може вести облік лише за журналом
            доходів і витрат — надходження від покупця одразу дохід, акти не потрібні. */}
        {(journalEligible || form.income_journal_only) && (
          <div style={{ margin: '0.75rem 0' }}>
            <label style={{ cursor: 'pointer', display: 'block' }}
              title="Надходження від покупців одразу стають доходом, а не авансом; реалізації й акти можна виписувати й друкувати, але борг покупця вони не створюють. У меню «Бухгалтерія» зʼявиться «Журнал доходів і витрат».">
              <input
                type="checkbox"
                checked={form.income_journal_only ?? false}
                disabled={!journalEligible && !form.income_journal_only}
                onChange={(e) => {
                  signalUserEdit();
                  setForm({ ...form, income_journal_only: e.target.checked,
                    income_journal_since: e.target.checked ? (form.income_journal_since ?? kyivToday()) : null });
                }}
                style={{ width: 'auto', marginRight: '0.4rem' }}
              />
              Облік лише за Журналом доходів і витрат
            </label>
            {form.income_journal_only && (
              <div className="doc-grid" style={{ marginTop: '0.4rem' }}>
                <div className="field" style={{ gridColumn: 'span 3' }}>
                  <label htmlFor="income-journal-since">З дати</label>
                  {journalSavedOn ? (
                    <input id="income-journal-since" readOnly disabled
                      value={(form.income_journal_since ?? '').split('-').reverse().join('.')}
                      title="Дату не змінити, поки облік за журналом увімкнено: документи вже проведено за нею. Щоб почати з іншої дати, зніміть галочку, збережіть і поставте знову." />
                  ) : (
                    <DatePopover value={form.income_journal_since ?? kyivToday()}
                      onChange={(v) => { signalUserEdit(); setForm({ ...form, income_journal_since: v || null }); }} />
                  )}
                </div>
                <div className="field" style={{ gridColumn: 'span 9', alignSelf: 'end' }}>
                  <small style={{ color: 'var(--muted)' }}>
                    {journalSavedOn
                      ? 'Надходження від покупців — одразу дохід, реалізації й акти не створюють боргу покупця.'
                      : 'Після збереження аванси покупців, що висять на цю дату, закриє бухгалтерська довідка (у дохід вони вже потрапили в місяці оплати, податок не зміниться).'}
                  </small>
                </div>
              </div>
            )}
            {!journalEligible && form.income_journal_only && (
              <small style={{ display: 'block', color: 'var(--badge-amber-fg)' }}>
                Облік лише за журналом — для ФОП 1–3 груп без ПДВ. Після збереження його буде вимкнено.
              </small>
            )}
          </div>
        )}

        {/* Облікові модулі — без рамки (рішення власника 2026-08-17). */}
        <div style={{ margin: '0.75rem 0' }}>
          <label style={{ cursor: 'pointer', display: 'block' }}>
            <input
              type="checkbox"
              checked={form.use_returnable_packaging ?? false}
              onChange={(e) => setForm({ ...form, use_returnable_packaging: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Облік зворотної (заставної) тари
          </label>
          {/* ч.1088 (вимога власника): роздрібна торгівля — як облік тари:
              галочка організації відкриває розділ «Роздріб» у меню (ч.1082).
              Раніше use_retail ставився лише шаблоном бізнесу при створенні. */}
          <label style={{ cursor: 'pointer', display: 'block', marginTop: '0.5rem' }}
            title="Відкриває розділ «Роздріб» у меню: торгові точки й каси, чеки, зміни, ціни, картки лояльності">
            <input
              type="checkbox"
              checked={form.use_retail ?? false}
              onChange={(e) => setForm({ ...form, use_retail: e.target.checked })}
              style={{ width: 'auto', marginRight: '0.4rem' }}
            />
            Роздрібна торгівля
          </label>
          {/* 0429: контроль підписання первинки — власні endpoint'и
              (зберігається одразу, не через форму орг). Для нової орг —
              disabled-заглушка (компонент потребує збережену орг). */}
          {orgIdForRefs ? <SignControlSettings orgId={orgIdForRefs} /> : (
            <label style={{ display: 'block', marginTop: '0.5rem', color: 'var(--muted)' }}>
              <input type="checkbox" disabled checked={false}
                style={{ width: 'auto', marginRight: '0.4rem' }} />
              Контроль підписання первинних документів
              <small style={{ marginLeft: 6 }}>(доступно після збереження)</small>
            </label>
          )}
          {/* Облік виробництва (рішення власника 2026-08-17): галочка =
              use_recipes (наявний org-флаг, СПЛЯЧИЙ — жоден posting/gating
              його не читає, перевірено). Вимкнена галочка НІЧОГО не блокує:
              виробничі документи працюють як раніше (SIMPLE-режим). Спосіб
              обліку показується при увімкненій галочці АБО коли орг вже на
              1C_WIP (мігровані з 1С); зняти галочку при 1C_WIP не можна —
              спершу перейти на спрощений (не ламаємо облік НЗВ). */}
          {(() => {
            const wip = form.production_costing_mode === '1C_WIP';
            const prodOn = (form.use_recipes ?? false) || wip;
            return (<>
              <label style={{ cursor: 'pointer', display: 'block', marginTop: '0.5rem' }}
                title={wip
                  ? 'Організація на повному обліку НЗВ — щоб вимкнути, спершу переключіть спосіб на спрощений'
                  : undefined}>
                <input
                  type="checkbox"
                  checked={prodOn}
                  disabled={wip}
                  onChange={(e) => {
                    if (!e.target.checked) {
                      // Зняття галочки блокує СТВОРЕННЯ нових виробничих
                      // документів (наявні лишаються видимими) — попередити.
                      void confirm(
                        'Вимкнути облік виробництва? Створення нових '
                        + 'виробничих документів (звіти, переробка, '
                        + 'комплектація, специфікації) буде заблоковано; '
                        + 'наявні документи залишаться доступними для '
                        + 'перегляду. Розділ «Виробництво» зникне з меню, '
                        + 'якщо жодна юрособа не веде виробництво.',
                      ).then((ok) => {
                        if (ok) setForm((f) => ({ ...f, use_recipes: false }));
                      });
                      return;
                    }
                    setForm({ ...form, use_recipes: true });
                  }}
                  style={{ width: 'auto', marginRight: '0.4rem' }}
                />
                Облік виробництва
              </label>
              {prodOn && (
                <div className="field" style={{ marginTop: '0.4rem', marginLeft: '1.5rem',
                  width: 520, maxWidth: '100%' }}>
                  <label>Спосіб обліку виробництва</label>
                  <select
                    value={form.production_costing_mode ?? 'SIMPLE'}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        production_costing_mode: e.target.value as 'SIMPLE' | '1C_WIP',
                      })
                    }
                  >
                    <option value="SIMPLE">
                      Спрощений — матеріали одразу в собівартість (без НЗВ)
                    </option>
                    <option value="1C_WIP">
                      Повний — облік незавершеного виробництва (НЗВ), як у 1С
                    </option>
                  </select>
                </div>
              )}
            </>);
          })()}
        </div>

        {/* Метод собівартості + переоцінка валюти — один рядок. ч.1391 (рішення власника): поле
            «Розрахунковий період для декретних» сховано — у всіх організацій 12 місяців (значення
            лишається у формі й зберігається як було). Переоцінка валюти (0440): закриття місяця
            створює її щомісяця або лише на кінець кварталу; ч.758: для нових — щокварталу. */}
        <div className="doc-grid">
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>{t('org.cost_method')} *</label>
            <select value={form.cost_method} onChange={(e) => setForm({ ...form, cost_method: e.target.value as 'FIFO' | 'WAVG' })}>
              <option value="WAVG">WAVG (середньозважена)</option>
              <option value="FIFO">FIFO</option>
            </select>
          </div>
          <div className="field" style={{ gridColumn: 'span 6' }}>
            <label>Переоцінка валюти у закритті місяця *</label>
            <select
              value={form.fx_revaluation_period ?? 'QUARTERLY'}
              onChange={(e) => setForm({
                ...form,
                fx_revaluation_period: e.target.value as 'MONTHLY' | 'QUARTERLY',
              })}
            >
              <option value="MONTHLY">щомісяця</option>
              <option value="QUARTERLY">щокварталу (на дату балансу)</option>
            </select>
          </div>
        </div>
        {/* ч.1386: фінансова звітність і податок на прибуток — лише юрособи. ч.1390 (рішення
            власника): унизу вкладки, обовʼязкові (сервер не приймає порожнє значення). */}
        {form.entity_type === 'LEGAL_ENTITY' && (
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label htmlFor="org-fin-report-form"
                title="Мікропідприємство — два з трьох: активи до 350 тис. євро, чистий дохід до 700 тис. євро, до 10 працівників. Мале — до 4 млн, 8 млн євро, 50 працівників. Інші подають повний комплект. Юрособи-єдинники 3 групи — форми мікропідприємства.">
                Форми фінансової звітності *
              </label>
              <select id="org-fin-report-form" value={form.fin_report_form ?? 'MICRO'}
                onChange={(e) => { signalUserEdit(); setForm({ ...form, fin_report_form: e.target.value as 'MICRO' | 'SMALL' | 'FULL' }); }}>
                <option value="MICRO">мікропідприємство — №1-мс, №2-мс</option>
                <option value="SMALL">мале підприємство — №1-м, №2-м</option>
                <option value="FULL">повна — Баланс №1, Звіт про фінрезультати №2</option>
              </select>
            </div>
            {form.tax_system === 'GENERAL' ? (
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label htmlFor="org-profit-tax-period"
                  title="Квартальна декларація — якщо бухгалтерський дохід за попередній рік понад 40 млн грн; інакше річна; у рік створення — річна. «Автоматично» рахує дохід минулого року з проводок — якщо минулий рік до сервісу не перенесено, оберіть вручну.">
                  Декларація з податку на прибуток *
                </label>
                <select id="org-profit-tax-period" value={form.profit_tax_period ?? 'AUTO'}
                  onChange={(e) => { signalUserEdit(); setForm({ ...form, profit_tax_period: e.target.value as 'AUTO' | 'QUARTER' | 'YEAR' }); }}>
                  <option value="AUTO">автоматично — за доходом попереднього року (понад 40 млн грн — квартальна)</option>
                  <option value="QUARTER">квартальна</option>
                  <option value="YEAR">річна</option>
                </select>
              </div>
            ) : (
              <div className="field" style={{ gridColumn: 'span 6', alignSelf: 'end' }}>
                <small style={{ color: 'var(--muted)' }}>
                  Декларацію з податку на прибуток подають організації на загальній системі оподаткування.
                </small>
              </div>
            )}
          </div>
        )}
        </>)}



        {/* === ТАБ: Коди (ОПФГ + КОАТУУ) === */}
        {showTab('codes') && (
          <div className="tabs__panel">
            {/* ч.758: код оргформи — з вкладки «Загальне» (read-only); КАТОТТГ одразу після. */}
            <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
              <div className="field" style={{ gridColumn: 'span 2' }}>
                <label>Код оргформи</label>
                <input value={form.opfg_code ?? ''} disabled placeholder="—"
                  title={form.opfg_name ?? 'Оргформа обирається на вкладці «Загальне»'} />
              </div>
              <div className="field" style={{ gridColumn: 'span 6' }}>
                <label>Код КАТОТТГ / КОАТУУ</label>
                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                  <input
                    value={form.koatuu_code ?? ''}
                    maxLength={19}
                    style={{ flex: 1, minWidth: 0,
                      ...(form.koatuu_code && !/^(\d{10}|UA\d{17})$/.test(form.koatuu_code)
                        ? { borderColor: '#d33' } : {}) }}
                    onChange={(e) => setForm({
                      ...form,
                      koatuu_code: e.target.value.toUpperCase().replace(/[^UA0-9]/g, '') || null,
                    })}
                  />
                  {/* 0433: підбір коду з державного кодифікатора. */}
                  <KatottgPicker onPick={(code) => setForm({ ...form, koatuu_code: code })} />
                </div>
              </div>
            </div>

            {/* КВЕД — перенесено з окремої вкладки (рішення власника 2026-08-17). */}
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>КВЕД</div>

            <div className="field">
              <label>Основний КВЕД * <small style={{ color: 'var(--muted)' }}>(формат XX.XX)</small></label>
              <input
                value={form.kved_main ?? ''}
                placeholder="62.01"
                maxLength={7}
                onChange={(e) => setForm({ ...form, kved_main: e.target.value || null })}
              />
              {form.kved_main && (
                <small style={{ color: isValidKved(form.kved_main) ? 'green' : '#d33' }}>
                  {isValidKved(form.kved_main) ? '✓ Формат правильний' : '✗ Очікувано XX.XX'}
                </small>
              )}
            </div>
            {form.kved_extra.length > 0 && (
              <div className="field" style={{ marginTop: '0.5rem' }}>
                <label>Додаткові КВЕД</label>
                {form.kved_extra.map((code, i) => (
                  <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <input
                      value={code}
                      placeholder="63.11"
                      maxLength={7}
                      onChange={(e) => {
                        const next = [...form.kved_extra];
                        next[i] = e.target.value;
                        setForm({ ...form, kved_extra: next });
                      }}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        signalUserEdit();
                        setForm({
                          ...form,
                          kved_extra: form.kved_extra.filter((_, j) => j !== i),
                        });
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              style={{ marginTop: '0.5rem' }}
              onClick={() => { signalUserEdit(); setForm({ ...form, kved_extra: [...form.kved_extra, ''] }); }}
            >
              + Додати КВЕД
            </button>
            </div>


          </div>
        )}


        {/* === ТАБ: Контакти (об'єднано адреси + контакти + відп.особи) === */}
        {showTab('contacts') && (
          <div className="tabs__panel">
            {/* Адреси */}
            <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc' }}>
              <legend>{form.entity_type === 'FOP' ? 'Адреси (реєстрація + місце ведення)' : 'Адреси'}</legend>
              <div className="field">
                <label>{form.entity_type === 'FOP' ? 'Адреса реєстрації (за пропискою)' : 'Юридична адреса'}</label>
                <input
                  value={form.legal_address ?? ''}
                  placeholder="м. Київ, вул. Хрещатик, 1, оф. 101"
                  onChange={(e) => setForm({ ...form, legal_address: e.target.value || null })}
                />
              </div>
              <div className="field" style={{ marginTop: '0.5rem' }}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.actual_address === null || form.actual_address === form.legal_address}
                    onChange={(e) => setForm({
                      ...form,
                      actual_address: e.target.checked ? form.legal_address : '',
                    })}
                    style={{ width: 'auto', marginRight: '0.5rem' }}
                  />
                  Фактична адреса співпадає з {form.entity_type === 'FOP' ? 'адресою реєстрації' : 'юридичною'}
                </label>
              </div>
              {form.actual_address !== null && form.actual_address !== form.legal_address && (
                <div className="field">
                  <label>Фактична адреса</label>
                  <input
                    value={form.actual_address ?? ''}
                    placeholder="м. Київ, вул. ..."
                    onChange={(e) => setForm({ ...form, actual_address: e.target.value || null })}
                  />
                </div>
              )}
            </fieldset>

            {/* Телефон/email */}
            <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc', marginTop: '0.75rem' }}>
              <legend>Контактні дані</legend>
              <div className="doc-grid">
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Телефон</label>
                  <input
                    type="tel"
                    value={form.phone ?? ''}
                    placeholder="+38 044 ___ __ __"
                    onChange={(e) => setForm({ ...form, phone: e.target.value || null })}
                  />
                </div>
                <div className="field" style={{ gridColumn: 'span 6' }}>
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email ?? ''}
                    placeholder="office@example.com"
                    onChange={(e) => setForm({ ...form, email: e.target.value || null })}
                  />
                </div>
              </div>
            </fieldset>

            {/* Відповідальні особи */}
            <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc', marginTop: '0.75rem' }}>
              <legend>Відповідальні особи</legend>
              {/* Керівник/головбух ведуться ЧЕРЕЗ КАДРИ (наказ прийому/
                  переміщення на посаду директора/головбуха). Тут поля read-only
                  для юрособи; редагування — на картці співробітника. Нова
                  юрособа — редагована (наказів ще немає).
                  ч.1235 (рішення власника): у ФОП відповідальна особа — САМ
                  підприємець: ПІБ = власна назва, РНОКПП = код організації.
                  Поля лише показують їх (тримає база, alembic 0600), з наказів
                  нічого не підтягується; звідси беруть підписанта звіти. */}
              {(() => {
                const isFop = form.entity_type === 'FOP';
                const readonly = isFop || (editing !== 'new' && form.entity_type === 'LEGAL_ENTITY');
                const headName = isFop ? fopHeadName(form) : (form.director_name ?? '');
                const headInn = isFop ? (form.inn ?? '') : (form.director_inn ?? '');
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem',
                      marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                      <small style={{ color: 'var(--muted)', flex: '1 1 300px' }}>
                        {isFop
                          ? 'Відповідальна особа ФОП — сам підприємець: ПІБ і РНОКПП беруться з назви '
                            + 'і коду на вкладці «Основне». Звідси вони потрапляють у звіти.'
                          : readonly
                          ? 'Заповнюються автоматично з кадрів (наказ прийому/переміщення '
                            + 'на посаду «Директор»/«Генеральний директор»/«Головний бухгалтер»). '
                            + 'Редагування — на картці співробітника. РНОКПП потрібен для звітів ДПС.'
                          : 'РНОКПП потрібен для звітів у ДПС.'}
                      </small>
                      {orgIdForRefs && !isFop && (
                        <button type="button" disabled={resolvingOfficials}
                          onClick={onResolveOfficials}
                          style={{ padding: '0.2rem 0.6rem', fontSize: '0.8rem' }}
                          title="Знайти керівника/головбуха у наявних проведених наказах">
                          {resolvingOfficials ? '…' : 'підтягнути з наказів'}
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <div className="field" style={{ flex: '1 1 260px' }}>
                        <label>{isFop ? 'ПІБ ФОП' : 'ПІБ керівника'}</label>
                        <input value={headName} disabled={readonly}
                          placeholder="Іваненко Іван Іванович"
                          style={readonly ? { background: 'var(--bg-soft-2)' } : undefined}
                          onChange={(e) => setForm({ ...form, director_name: e.target.value || null })} />
                      </div>
                      <div className="field" style={{ width: 160 }}>
                        <label>{isFop ? 'РНОКПП ФОП' : 'РНОКПП керівника'}</label>
                        <input value={headInn} maxLength={10} disabled={readonly}
                          placeholder="10 цифр"
                          style={readonly ? { background: 'var(--bg-soft-2)' } : undefined}
                          onChange={(e) => setForm({ ...form,
                            director_inn: e.target.value.replace(/\D/g, '') || null })} />
                      </div>
                    </div>
                    {form.entity_type === 'LEGAL_ENTITY' && (
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                        <div className="field" style={{ flex: '1 1 260px' }}>
                          <label>ПІБ головного бухгалтера</label>
                          <input value={form.chief_accountant ?? ''} disabled={readonly}
                            style={readonly ? { background: 'var(--bg-soft-2)' } : undefined}
                            onChange={(e) => setForm({ ...form, chief_accountant: e.target.value || null })} />
                        </div>
                        <div className="field" style={{ width: 160 }}>
                          <label>РНОКПП головбуха</label>
                          <input value={form.chief_accountant_inn ?? ''} maxLength={10} disabled={readonly}
                            placeholder="10 цифр"
                            style={readonly ? { background: 'var(--bg-soft-2)' } : undefined}
                            onChange={(e) => setForm({ ...form,
                              chief_accountant_inn: e.target.value.replace(/\D/g, '') || null })} />
                        </div>
                        <label style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: '0.4rem',
                          cursor: readonly ? 'default' : 'pointer' }}
                          title="Без галочки у підписах декларацій ДПС (ПДВ, єдиний податок, J05, повідомлення про прийняття) головбуха немає — підписує лише керівник">
                          <input type="checkbox" checked={!!form.chief_accountant_signs_reports}
                            disabled={readonly || !form.chief_accountant}
                            style={{ width: 'auto' }}
                            onChange={(e) => setForm({ ...form, chief_accountant_signs_reports: e.target.checked })} />
                          використовувати в регламентованих звітах
                        </label>
                      </div>
                    )}
                  </>
                );
              })()}
            </fieldset>
          </div>
        )}

        {/* === ТАБ: Банк (IBAN + currency + auto-bank-from-MFO) === */}
        {showTab('bank') && (
          <div className="tabs__panel">
            {editing === 'new' ? (
              /* Створення: 1 inline bank-account оптіонально */
              <BankAccountInlineForm
                iban={newBankIban}
                onIbanChange={setNewBankIban}
                currencyId={newBankCurrencyId}
                onCurrencyChange={setNewBankCurrencyId}
                accountName={newBankAccountName}
                onAccountNameChange={setNewBankAccountName}
                isMain={newBankIsMain}
                onIsMainChange={setNewBankIsMain}
                currencies={currenciesQ.data ?? []}
                banks={banksQ.data ?? []}
                title="Перший банк-рахунок (опціонально)"
                helpText="Можна залишити порожнім — додасте після створення."
              />
            ) : (
              /* Редагування: список наявних + кнопка + Додати */
              <>
                {/* Error UI для bank-account мутацій (2026-06-04 hotfix). */}
                {(addBankAccount.isError || setMainBankAccount.isError
                  || removeBankAccount.isError) && (
                  <div style={{
                    background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', padding: '0.75rem',
                    borderRadius: 6, marginBottom: '0.75rem', color: '#b00',
                  }}>
                    <strong>❌ Помилка банк-рахунку:</strong>
                    <div style={{ marginTop: '0.25rem', fontSize: '0.9em' }}>
                      {formatSaveError(
                        addBankAccount.error ?? setMainBankAccount.error
                        ?? removeBankAccount.error
                      )}
                    </div>
                  </div>
                )}
                <BankAccountDuplicates
                  accounts={bankAccountsQ.data ?? []}
                  currencies={currenciesQ.data ?? []}
                  onMerged={() => qc.invalidateQueries({ queryKey: ['bank-accounts'] })}
                />
                <BankAccountsEditList
                  accounts={bankAccountsQ.data ?? []}
                  currencies={currenciesQ.data ?? []}
                  banks={banksQ.data ?? []}
                  onAdd={(p) => addBankAccount.mutateAsync(p)}
                  onRemove={(id) => removeBankAccount.mutate(id)}
                  onSetMain={(id) => setMainBankAccount.mutate(id)}
                  addPending={addBankAccount.isPending}
                />
              </>
            )}
          </div>
        )}

        {/* === ТАБ: Склади === */}
        {showTab('warehouses') && (
          <div className="tabs__panel">
            {editing === 'new' || !orgIdForRefs ? (
              <p style={{ color: 'var(--muted)' }}>
                Спершу збережіть організацію — потім зможете додати склади.
              </p>
            ) : (
              <OrgWarehousesTab
                orgId={orgIdForRefs}
                canManage={canManageRefs}
                cantReason={refsCantReason}
              />
            )}
          </div>
        )}

        {/* === ТАБ: Каси === */}
        {showTab('cash-registers') && (
          <div className="tabs__panel">
            {editing === 'new' || !orgIdForRefs ? (
              <p style={{ color: 'var(--muted)' }}>
                Спершу збережіть організацію — потім зможете додати каси.
              </p>
            ) : (
              <OrgCashRegistersTab
                orgId={orgIdForRefs}
                canManage={canManageRefs}
                cantReason={refsCantReason}
              />
            )}
          </div>
        )}

        {/* === ТАБ: Податкові рахунки (отримувач ПДФО/ВЗ/ЄСВ для перерахування) === */}
        {showTab('tax') && (
          <div className="tabs__panel">
            {/* ч.744 (рішення власника): без підказок, рамок і зайвих ліній.
                ДПІ — пошук зі справочника (код C_STI_ORIG, область C_REG, назва HSTI). */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="field" style={{ flex: '1 1 360px', maxWidth: 560, position: 'relative' }}>
                <label>Податкова інспекція (ДПІ)</label>
                <input
                  value={stiOpen ? stiSearch : (form.sti_name
                    ? `${form.sti_code ?? ''} — ${form.sti_name}` : '')}
                  placeholder="назва, код або область"
                  onFocus={() => { setStiOpen(true); setStiSearch(''); }}
                  onChange={(e) => { setStiSearch(e.target.value); setStiOpen(true); }}
                  onBlur={() => setTimeout(() => setStiOpen(false), 150)} />
                {stiOpen && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0,
                    zIndex: 20, background: 'var(--bg-panel)', border: '1px solid var(--border)',
                    borderRadius: 6, marginTop: 2, maxHeight: 240, overflow: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    {(stiQ.data?.items ?? []).map((o) => (
                      <div key={o.code}
                        onMouseDown={() => {
                          signalUserEdit();
                          setForm({ ...form,
                            sti_code: o.code,
                            sti_name: o.name,
                            sti_region: o.code.length >= 3
                              ? Number(o.code.slice(0, -2)) : form.sti_region });
                          setStiOpen(false);
                        }}
                        style={{ padding: '0.4rem 0.6rem', cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9' }}>
                        <b>{o.code}</b> — {o.name}
                        {o.region_name && (
                          <span style={{ color: 'var(--muted)' }}> · {o.region_name}</span>
                        )}
                      </div>
                    ))}
                    {stiQ.data && stiQ.data.items.length === 0 && (
                      <div style={{ padding: '0.5rem', color: 'var(--muted)' }}>
                        Нічого не знайдено (Довідники → Органи ДПС).
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="field" style={{ width: 120 }}>
                <label>Код ДПІ</label>
                <input disabled style={{ background: 'var(--bg-soft-2)' }}
                  value={form.sti_code ?? ''} placeholder="—" />
              </div>
            </div>

            {/* Податкові рахунки: 3 системні (ЗП) + користувацькі рядки — спільна
                таблиця з майстром (крок 3). */}
            <OrgTaxAccountsTable form={form} setForm={setForm}
              banks={banksQ.data ?? []} style={{ marginTop: '0.75rem', maxWidth: 1400 }} />
            {/* ч.806: місця діяльності (відокремлені підрозділи) — лише юрособи. */}
            {form.entity_type === 'LEGAL_ENTITY' && (
              <OrgActivityPlacesTable form={form} setForm={setForm}
                banks={banksQ.data ?? []} style={{ marginTop: '1.25rem', maxWidth: 1400 }} />
            )}
          </div>
        )}
        {/* 0452 (рішення власника 2026-09-01): вкладка ЕДО — бейдж
            заповненості сховища вкладень + галка «зберігати документи
            Кабінету ДПС» (перегляд з ключем архівує PDF → далі без ключа). */}
        {orgTab === 'edo' && (
          <div className="tabs__panel">
            {editing !== 'new' && editing && <OrgEdoStorageBadge orgId={editing.id} />}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8,
              marginTop: '0.75rem', cursor: 'pointer' }}>
              <input type="checkbox"
                checked={form.dps_store_docs ?? false}
                onChange={(e) => setForm({ ...form, dps_store_docs: e.target.checked })} />
              Зберігати документи Кабінету ДПС
            </label>
            <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0.4rem 0 0 26px' }}>
              Коли увімкнено: документ із вкладок «Вхідні» та «Звітність»
              Кабінету ДПС, ХОЧ РАЗ переглянутий користувачем з ключем КЕП,
              зберігається у сховищі цієї організації (місце «Кабінет ДПС» у
              профілі) — і далі відкривається БЕЗ ключа будь-ким, хто має
              доступ до вкладень. Ще не переглянуті документи, як і раніше,
              потребують ключа. Файли рахуються у квоті сховища (1 ГБ).
              Не забудьте зберегти організацію після зміни галки.
            </p>
          </div>
        )}
        {orgTab === 'stamps' && (
          editing !== 'new' && editing ? (
            <OrgStamps orgId={editing.id} />
          ) : (
            <div className="tabs__panel">
              <p style={{ color: 'var(--muted)' }}>
                Спершу збережіть організацію — потім зможете завантажити
                печатку та підпис.
              </p>
            </div>
          )
        )}
      </div>

      <ConfirmModuleNarrowingModal
        open={templateSwitchConfirm.open}
        title="Перемикання бізнес-шаблону юрособи"
        removedModules={templateSwitchConfirm.removedModules}
        affected={templateSwitchConfirm.affected}
        isLoading={templateSwitchConfirm.isLoading}
        onConfirm={confirmTemplateSwitch}
        onCancel={() =>
          setTemplateSwitchConfirm((s) => ({ ...s, open: false }))
        }
      />
    </DraftSlotsProvider>
  );
}


// ===========================================================================
// ORG-EDITOR.1 (2026-06-04): inline компоненти для табу «Банк».
// ===========================================================================

interface CurrencyOption {
  id: string;
  code: string;
  name: string;
}

interface BankAccountInlineFormProps {
  iban: string;
  onIbanChange: (v: string) => void;
  currencyId: string;
  onCurrencyChange: (id: string) => void;
  accountName: string;
  onAccountNameChange: (v: string) => void;
  /** Опціонально показати чекбокс "Основний рахунок" (тільки при створенні
   *  нового org або коли додаємо у org що ще без рахунків — інакше у списку
   *  є кнопка Зробити основним).
   */
  isMain?: boolean;
  onIsMainChange?: (v: boolean) => void;
  currencies: CurrencyOption[];
  banks: BankRow[];
  title: string;
  helpText?: string;
}

/** Форма для одного inline bank-account (при створенні org або при +Додати). */
function BankAccountInlineForm({
  iban, onIbanChange, currencyId, onCurrencyChange,
  accountName, onAccountNameChange,
  isMain, onIsMainChange,
  currencies, banks, title, helpText,
}: BankAccountInlineFormProps) {
  const bankName = findBankByIban(iban, banks);
  const ibanValid = isValidUaIban(iban);

  // Auto-default UAH currency через useEffect (не у render!).
  // 2026-06-04 hotfix: попередня версія робила setTimeout прямо у render
  // що викликало race condition і нескінченний loop при певних умовах.
  useEffect(() => {
    if (!currencyId && currencies.length > 0) {
      const uah = currencies.find((c) => c.code === 'UAH');
      if (uah) onCurrencyChange(uah.id);
    }
  }, [currencyId, currencies, onCurrencyChange]);

  return (
    <fieldset style={{ padding: '0.75rem', border: '1px solid #ccc' }}>
      <legend>{title}</legend>
      {helpText && (
        <p style={{ marginTop: 0, color: 'var(--muted)', fontSize: '0.9em' }}>
          {helpText}
        </p>
      )}
      <div className="field">
        <label>IBAN <small style={{ color: 'var(--muted)' }}>(UA + 27 цифр)</small></label>
        <input
          value={formatIban(iban)}
          placeholder="UA71 339500 00000 2600 8370 2300 01"
          onChange={(e) => onIbanChange(e.target.value)}
          style={{ fontFamily: 'monospace' }}
        />
        {iban.replace(/\s+/g, '').length > 0 && (
          <small style={{ color: ibanValid ? 'green' : '#d33' }}>
            {ibanValid
              ? bankName
                ? `✓ Банк: ${bankName}`
                : '✓ IBAN валідний (банк не у довіднику НБУ — введіть назву рахунку вручну)'
              : `✗ Формат UA + 27 цифр (зараз ${iban.replace(/\s+/g, '').length})`
            }
          </small>
        )}
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Валюта *</label>
          <select
            value={currencyId}
            onChange={(e) => onCurrencyChange(e.target.value)}
          >
            <option value="">— Не обрано —</option>
            {currencies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} — {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Назва рахунку <small style={{ color: 'var(--muted)' }}>(опціонально)</small></label>
          <input
            value={accountName}
            placeholder={bankName ? `Основний рахунок ${bankName}` : 'Основний'}
            onChange={(e) => onAccountNameChange(e.target.value)}
          />
        </div>
      </div>
      {onIsMainChange !== undefined && (
        <div className="field" style={{ marginTop: '0.5rem' }}>
          <label style={{ cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isMain ?? false}
              onChange={(e) => onIsMainChange(e.target.checked)}
              style={{ width: 'auto', marginRight: '0.5rem' }}
            />
            Основний рахунок організації
            <small style={{ color: 'var(--muted)', marginLeft: '0.5rem' }}>
              (за замовч. використовується у платежах)
            </small>
          </label>
        </div>
      )}
    </fieldset>
  );
}


interface BankAccountsEditListProps {
  accounts: BankAccount[];
  currencies: CurrencyOption[];
  banks: BankRow[];
  onAdd: (p: { iban: string; currency_id: string; account_name: string | null }) => Promise<unknown>;
  onRemove: (id: string) => void;
  onSetMain: (id: string) => void;
  addPending: boolean;
}

/** Список наявних bank-accounts + форма для додавання нового. */
function BankAccountsEditList({
  accounts, currencies, banks,
  onAdd, onRemove, onSetMain, addPending,
}: BankAccountsEditListProps) {
  // ч.1060: рядок нового рахунку — у слотах картки (переживає перемикання
  // вкладок і входить у чернетку); очищаємо ЛИШЕ після успішного додавання —
  // при помилці введене лишається у формі.
  const [showAddForm, setShowAddForm] = useDraftSlot('orgBank.showAdd', false);
  const [newIban, setNewIban] = useDraftSlot('orgBank.iban', '');
  const [newCurrencyId, setNewCurrencyId] = useDraftSlot('orgBank.currencyId', '');
  const [newName, setNewName] = useDraftSlot('orgBank.name', '');
  usePendingMarker('orgBank', showAddForm && (newIban.trim() !== '' || newName.trim() !== '')
    ? 'новий банківський рахунок' : null);

  const canAdd =
    isValidUaIban(newIban) && newCurrencyId !== '';

  function handleAdd() {
    if (!canAdd) return;
    onAdd({
      iban: newIban,
      currency_id: newCurrencyId,
      account_name: newName.trim() || null,
    }).then(() => {
      setNewIban(''); setNewCurrencyId(''); setNewName('');
      setShowAddForm(false);
    }).catch(() => { /* помилку показує картка; введене лишаємо */ });
  }

  return (
    <>
      {accounts.length === 0 && !showAddForm && (
        <p style={{ color: 'var(--muted)' }}>
          Немає банк-рахунків. Натисніть «+ Додати» щоб створити.
        </p>
      )}
      {accounts.length > 0 && (
        <TchTable style={{ marginBottom: '0.75rem' }}>
          <thead>
            <tr>
              <th style={{ width: 60, textAlign: 'center' }}>Осн.</th>
              <th>IBAN</th>
              <th>Банк</th>
              <th>Назва</th>
              <th style={{ width: 80 }}>Валюта</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((acc) => {
              const bankName = findBankByIban(acc.iban, banks);
              const curr = currencies.find((c) => c.id === acc.currency_id);
              return (
                <tr key={acc.id}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="radio"
                      name="bank-is-main"
                      checked={acc.is_main}
                      onChange={() => {
                        if (!acc.is_main) onSetMain(acc.id);
                      }}
                      title={acc.is_main ? 'Це основний рахунок' : 'Зробити основним'}
                      style={{ width: 'auto', cursor: 'pointer', margin: 0, verticalAlign: 'middle' }}
                    />
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatIban(acc.iban)}
                    {(acc.same_iban_cards ?? 0) > 0 && (
                      <span title="Той самий рахунок заведено кількома картками — обʼєднайте їх вище"
                        style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--badge-amber-fg)',
                          background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
                          borderRadius: 4, padding: '0 4px' }}>дубль</span>
                    )}
                  </td>
                  <td title="Назва банку — з довідника за МФО IBAN">
                    {bankName ?? <span style={{ color: 'var(--muted)' }}>—</span>}
                  </td>
                  <td>
                    {acc.account_name ?? '—'}
                  </td>
                  <td>{curr?.code ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="danger"
                      title="Видалити"
                      onClick={() => {
                        if (confirm(`Видалити рахунок ${formatIban(acc.iban)}?`)) {
                          onRemove(acc.id);
                        }
                      }}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
      {!showAddForm ? (
        <button type="button" onClick={() => setShowAddForm(true)}>
          + Додати банк-рахунок
        </button>
      ) : (
        <>
          <BankAccountInlineForm
            iban={newIban}
            onIbanChange={setNewIban}
            currencyId={newCurrencyId}
            onCurrencyChange={setNewCurrencyId}
            accountName={newName}
            onAccountNameChange={setNewName}
            currencies={currencies}
            banks={banks}
            title="Новий банк-рахунок"
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button type="button" onClick={() => {
              setShowAddForm(false); setNewIban(''); setNewCurrencyId(''); setNewName('');
            }}>
              Скасувати
            </button>
            <button
              type="button"
              className="primary"
              disabled={addPending}
              onClick={handleAdd}
              title={!canAdd ? 'Заповни IBAN (UA + 27 цифр) і вибери валюту' : undefined}
              style={{ opacity: canAdd ? 1 : 0.6 }}
            >
              {addPending ? 'Зберігаємо…' : 'Зберегти рахунок'}
            </button>
          </div>
        </>
      )}
    </>
  );
}


/** 0452: бейдж заповненості сховища вкладень org (вкладка ЕДО). */
function OrgEdoStorageBadge({ orgId }: { orgId: string }) {
  const usageQ = useQuery({
    queryKey: ['attachments-storage-usage'],
    queryFn: () => attachments.storageUsage(),
  });
  const u = usageQ.data?.find((x) => x.organization_id === orgId);
  const used = u?.used_bytes ?? 0;
  const quota = u?.quota_bytes ?? 1024 ** 3;
  const pct = Math.min(100, Math.round((used / quota) * 100));
  const color = pct >= 90 ? '#dc2626' : pct >= 70 ? '#b45309' : '#2563eb';
  const dps = u?.modules.find((m) => m.module === 'dps_cabinet');
  const fmt = (n: number) => n >= 1024 ** 3 ? `${(n / 1024 ** 3).toFixed(2)} ГБ`
    : n >= 1024 ** 2 ? `${(n / 1024 ** 2).toFixed(1)} МБ`
    : `${Math.ceil(n / 1024)} КБ`;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8,
      border: '1px solid var(--border-soft)', borderRadius: 6,
      padding: '0.3rem 0.6rem', fontSize: '0.85rem' }}>
      <span>Сховище вкладень:</span>
      <span style={{ width: 90, height: 7, background: 'var(--border)', borderRadius: 4 }}>
        <span style={{ display: 'block', width: `${Math.max(pct, used > 0 ? 3 : 0)}%`,
          height: '100%', background: color, borderRadius: 4 }} />
      </span>
      <b style={{ color }}>{pct}%</b>
      <span style={{ color: 'var(--muted)' }}>
        {fmt(used)} з {fmt(quota)}
        {dps ? ` · Кабінет ДПС: ${dps.files} файл(ів), ${fmt(dps.bytes)}` : ''}
      </span>
    </div>
  );
}
